import React from 'react';
import { Link } from 'react-router-dom';
import { LogIn, UserPlus, Shield, Users, Globe, Heart } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import PadaidLogo from '../../components/ui/PadaidLogo';
import SocialLoginButtons from '../../components/auth/SocialLoginButtons';

const AuthLanding = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-primary/5">
      {/* Header */}
      <header className="bg-card/50 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center py-6">
            <PadaidLogo size="lg" showText={true} />
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <Badge variant="outline" className="text-primary border-primary/20 bg-primary/5">
              <Globe className="w-4 h-4 mr-2" />
              Pour un Avenir Durable et une Aide Internationale Digne
            </Badge>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
            Bienvenue sur{' '}
            <div className="padaid-logo inline-block">
              <span className="pa-red">PA</span>
              <span className="daid-green">DAID</span>
            </div>
          </h1>
          
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Rejoignez notre communauté engagée pour partager ce que nous avons acquis à ce que nous espérons. 
            Connectez vous ou créez votre compte pour accéder à toutes nos ressources.
          </p>
        </div>

        {/* Authentication Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Login Card */}
          <Card className="border-primary/20 hover:shadow-lg transition-all duration-300 hover:border-primary/40">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <LogIn className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">Se connecter</CardTitle>
              <CardDescription>
                Vous avez déjà un compte ? Connectez-vous pour accéder à votre espace personnel
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full bg-primary hover:bg-primary/90 text-lg py-6" asChild>
                <Link to="/login">
                  <LogIn className="mr-2 h-5 w-5" />
                  Connexion
                </Link>
              </Button>
              
              <div className="mt-4 text-center">
                <p className="text-sm text-muted-foreground">
                  Accédez directement à vos projets, donations et tableau de bord personnalisé
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Register Card */}
          <Card className="border-secondary/20 hover:shadow-lg transition-all duration-300 hover:border-secondary/40">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserPlus className="h-8 w-8 text-secondary" />
              </div>
              <CardTitle className="text-2xl">S'inscrire</CardTitle>
              <CardDescription>
                Nouveau sur PADAID ? Créez votre compte et rejoignez notre mission
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                variant="outline" 
                className="w-full border-secondary text-secondary hover:bg-secondary hover:text-black text-lg py-6" 
                asChild
              >
                <Link to="/register">
                  <UserPlus className="mr-2 h-5 w-5" />
                  Inscription
                </Link>
              </Button>
              
              <div className="mt-4 text-center">
                <p className="text-sm text-muted-foreground">
                  Choisissez votre rôle et commencez votre engagement humanitaire
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Social Login Section */}
        <div className="text-center mb-12">
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border/50" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-background text-muted-foreground">Ou connectez-vous avec</span>
            </div>
          </div>
          
          <SocialLoginButtons />
        </div>

        {/* Features Preview */}
        <div className="bg-muted/30 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-center mb-8">Ce qui vous attend après connexion</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Heart className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Projets Humanitaires</h3>
              <p className="text-sm text-muted-foreground">
                Découvrez et soutenez nos projets de développement en Afrique
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Users className="h-6 w-6 text-secondary" />
              </div>
              <h3 className="font-semibold mb-2">Communauté Active</h3>
              <p className="text-sm text-muted-foreground">
                Rejoignez une communauté de membres engagés et de bénévoles
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Globe className="h-6 w-6 text-accent" />
              </div>
              <h3 className="font-semibold mb-2">Impact Global</h3>
              <p className="text-sm text-muted-foreground">
                Suivez l'impact de vos contributions et de nos actions
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-destructive/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Shield className="h-6 w-6 text-destructive" />
              </div>
              <h3 className="font-semibold mb-2">Sécurité Avancée</h3>
              <p className="text-sm text-muted-foreground">
                Protection 2FA et gestion sécurisée de vos données
              </p>
            </div>
          </div>
        </div>

        {/* User Types Info */}
        <Card className="bg-gradient-to-r from-muted/50 to-muted/30">
          <CardHeader>
            <CardTitle className="text-center">Types de comptes disponibles</CardTitle>
            <CardDescription className="text-center">
              Choisissez le niveau d'engagement qui vous correspond
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-4 rounded-lg bg-primary/10 border border-primary/20">
                <Users className="h-8 w-8 text-primary mx-auto mb-2" />
                <div className="font-medium text-primary mb-1">Membre</div>
                <div className="text-xs text-muted-foreground">
                  Accès complet aux ressources et projets
                </div>
              </div>
              
              <div className="text-center p-4 rounded-lg bg-secondary/10 border border-secondary/20">
                <Users className="h-8 w-8 text-secondary mx-auto mb-2" />
                <div className="font-medium text-secondary mb-1">Invité</div>
                <div className="text-xs text-muted-foreground">
                  Découverte et participation limitée
                </div>
              </div>
              
              <div className="text-center p-4 rounded-lg bg-accent/10 border border-accent/20">
                <Shield className="h-8 w-8 text-accent mx-auto mb-2" />
                <div className="font-medium text-accent mb-1">Co-fondateur</div>
                <div className="text-xs text-muted-foreground">
                  Privilèges étendus et responsabilités
                </div>
              </div>
              
              <div className="text-center p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                <Shield className="h-8 w-8 text-destructive mx-auto mb-2" />
                <div className="font-medium text-destructive mb-1">Fondateur</div>
                <div className="text-xs text-muted-foreground">
                  Contrôle complet et administration
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-border">
          <p className="text-muted-foreground">
            En vous connectant, vous acceptez nos conditions d'utilisation et notre politique de confidentialité.
          </p>
          <div className="mt-4">
            <Badge variant="outline" className="text-xs">
              PADAID.COM - Plateforme sécurisée avec authentification 2FA
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthLanding;